<template>
  <section class="safe-inspector border rounded-3 p-3 p-md-4 mb-4" aria-labelledby="safe-inspector-title">
    <div class="d-flex flex-column flex-md-row justify-content-between gap-2 mb-3">
      <div>
        <h2 id="safe-inspector-title" class="h4 mb-1">Automatic connection check</h2>
        <p class="mb-0 text-muted">
          Checks that both parts of Biotron can talk to this browser and that its saved settings can be read. Nothing is changed.
        </p>
      </div>
      <button v-if="standaloneDownload" type="button" class="btn btn-outline-primary align-self-md-start"
              :disabled="!session" @click="downloadReport">
        Download this section
      </button>
    </div>

    <div class="alert py-2" :class="headlineClass" role="status" aria-live="polite">
      {{ headline }}
    </div>

    <button type="button" class="btn btn-primary mb-3" @click="startInspection"
            :disabled="!canStart">
      {{ running ? "Checking both Biotron ports…" : "Check the connection" }}
    </button>

    <details v-if="session" class="safe-details" :open="summary !== 'pass'">
      <summary class="fw-semibold">See what was checked</summary>
      <ol class="list-group list-group-numbered mt-2">
        <li v-for="probe in probes" :key="probe.id"
            class="list-group-item d-flex justify-content-between align-items-center gap-3">
          <span class="ms-2 me-auto">{{ probe.label }}</span>
          <span class="badge rounded-pill" :class="statusClass(probeStatus(probe.id))">
            {{ statusLabel(probeStatus(probe.id)) }}
          </span>
        </li>
      </ol>
    </details>

    <p class="small text-muted mt-3 mb-0">
      Next, the page will ask you to touch the plant, use the light sensor and check the controls you can see or hear.
    </p>
  </section>
</template>

<script>
import {
  BIOTRON_SAFE_INSPECTOR_PROBES,
  BIOTRON_REVIEWED_FIRMWARE_VERSION,
  BIOTRON_DIAGNOSTICS_LOCK_NAME,
  appendBiotronInspectorEvidence,
  biotronInspectorSummary,
  createBiotronInspectorReport,
  createBiotronInspectorSession,
  invalidateBiotronInspectorSession,
  sha256Hex,
  biotronInspectorFirmwareMatches
} from "@/assets/js/BiotronSafeInspector"

const randomSessionId = () => {
  const values = new Uint32Array(2)
  crypto.getRandomValues(values)
  return Array.from(values, value => value.toString(16).padStart(8, "0")).join("")
}

export default {
  name: "BiotronSafeInspector",
  props: {
    deviceConnected: {type: Boolean, default: false},
    firmwareVersion: {type: String, default: ""},
    settingsReadbackLoaded: {type: Boolean, default: false},
    deviceEpoch: {type: Number, default: 0},
    inspectionResult: {type: Object, default: null},
    buildId: {type: String, default: ""},
    mutationEpoch: {type: Number, default: 0},
    standaloneDownload: {type: Boolean, default: true}
  },
  emits: ["inspection_requested", "inspection_state_changed", "inspection_summary_changed", "inspector_report_changed"],
  data() {
    return {
      probes: BIOTRON_SAFE_INSPECTOR_PROBES,
      session: null,
      ports: [],
      running: false,
      tabLock: "pending",
      releaseTabLock: null
    }
  },
  computed: {
    canStart() {
      return this.tabLock === "pass" && this.deviceConnected &&
        this.firmwareVersion === BIOTRON_REVIEWED_FIRMWARE_VERSION && this.settingsReadbackLoaded && !this.running
    },
    summary() {
      return biotronInspectorSummary(this.session)
    },
    headline() {
      if (this.tabLock === "blocked") return "This check cannot start because another test page is open. Close the other page, then retry."
      if (this.session && !this.session.valid) return "This result expired because Biotron or the browser connection changed. Start the connection check again."
      if (!this.deviceConnected) return "Connect one Biotron to continue."
      if (this.firmwareVersion !== BIOTRON_REVIEWED_FIRMWARE_VERSION) return `This check has only been reviewed with firmware ${BIOTRON_REVIEWED_FIRMWARE_VERSION}. Detected: ${this.firmwareVersion || "not detected"}.`
      if (!this.settingsReadbackLoaded) return "Connected. Waiting for Biotron to return its saved settings…"
      if (this.running) return "Checking both Biotron ports without changing anything…"
      if (this.summary === "pass") return "Connection looks good — both Biotron ports replied and the saved settings are readable."
      if (this.summary === "fail") return "The connection check found a problem. Stop here and download the report for support."
      return "Ready. This check only reads information from Biotron."
    },
    headlineClass() {
      if (this.summary === "pass") return "alert-success"
      if (this.summary === "fail" || this.tabLock === "blocked") return "alert-danger"
      return "alert-secondary"
    }
  },
  watch: {
    summary(value) {
      this.$emit("inspection_summary_changed", value)
    },
    deviceConnected(connected) {
      if (!connected) this.invalidate("device_disconnected")
    },
    firmwareVersion(value, previous) {
      if (this.session && value !== previous) this.invalidate("firmware_version_changed")
    },
    settingsReadbackLoaded(loaded) {
      if (!loaded) this.invalidate("settings_readback_lost")
    },
    mutationEpoch(value, previous) {
      if (this.session && value !== previous) this.invalidate("device_state_changed")
    },
    deviceEpoch(value, previous) {
      if (this.session && value !== previous && value !== this.session.device_epoch) {
        this.invalidate("device_session_changed")
      }
    },
    inspectionResult(result) {
      this.consumeInspectionResult(result)
    },
    session() { this.emitReport() },
    ports() { this.emitReport() }
  },
  mounted() {
    this.acquireTabLock()
  },
  beforeUnmount() {
    this.invalidate("inspector_closed")
    if (this.releaseTabLock) this.releaseTabLock()
  },
  methods: {
    async acquireTabLock() {
      if (!navigator.locks || typeof navigator.locks.request !== "function") {
        this.tabLock = "blocked"
        return
      }
      const hold = new Promise(resolve => { this.releaseTabLock = resolve })
      navigator.locks.request(BIOTRON_DIAGNOSTICS_LOCK_NAME, {ifAvailable: true}, async lock => {
        if (!lock) {
          this.tabLock = "blocked"
          return
        }
        this.tabLock = "pass"
        await hold
      }).catch(() => { this.tabLock = "blocked" })
    },
    startInspection() {
      if (!this.canStart) return
      let session = createBiotronInspectorSession({
        sessionId: randomSessionId(), buildId: this.buildId,
        deviceEpoch: this.deviceEpoch
      })
      session = appendBiotronInspectorEvidence(session, {
        probe: "tab_lock", status: "pass", kind: "environment",
        reason: "exclusive_origin_lock_acquired"
      })
      session = appendBiotronInspectorEvidence(session, {
        probe: "device_session", status: "pass", kind: "automatic_probe",
        reason: "fresh_connected_session"
      })
      this.session = session
      this.ports = []
      this.running = true
      this.$emit("inspection_state_changed", true)
      this.$emit("inspection_requested", {
        sessionId: session.session_id,
        deviceEpoch: session.device_epoch
      })
    },
    consumeInspectionResult(result) {
      if (!this.session || !this.running || !result ||
          result.sessionId !== this.session.session_id ||
          result.deviceEpoch !== this.session.device_epoch) return

      this.running = false
      this.$emit("inspection_state_changed", false)
      if (result.status !== "pass") {
        const probe = this.probeForFailure(result.reason)
        this.session = appendBiotronInspectorEvidence(this.session, {
          probe, status: "blocked", kind: "blocked",
          reason: result.reason || "inspection_blocked"
        })
        return
      }

      this.ports = result.ports || []
      const topologyPass = this.ports.length === 2
      const versions = this.ports.map(port => port.firmware).filter(Boolean)
      const versionPass = topologyPass && biotronInspectorFirmwareMatches(versions)
      const fingerprints = this.ports.map(port => port.settingsFingerprint).filter(Boolean)
      const readbackPass = topologyPass && fingerprints.length === 2 &&
        new Set(fingerprints).size === 1 && this.ports.every(port => port.settingsClean &&
          port.dirtyGeneration === port.persistedGeneration)
      this.session = appendBiotronInspectorEvidence(this.session, {
        probe: "port_topology", status: topologyPass ? "pass" : "fail",
        kind: "automatic_probe", reason: topologyPass ? "two_matched_pairs" : "unexpected_port_count"
      })
      this.session = appendBiotronInspectorEvidence(this.session, {
        probe: "firmware", status: versionPass ? "pass" : "fail",
        kind: "automatic_probe", reason: versionPass ? "fresh_reviewed_version_replies" : "missing_mismatched_or_unsupported_version"
      })
      this.session = appendBiotronInspectorEvidence(this.session, {
        probe: "persisted_readback", status: readbackPass ? "pass" : "fail",
        kind: "automatic_probe", reason: readbackPass ? "clean_matching_generations_and_values" : "dirty_invalid_or_mismatched_readback"
      })
    },
    invalidate(reason) {
      if (this.session) this.session = invalidateBiotronInspectorSession(this.session, reason)
      if (this.running) this.$emit("inspection_state_changed", false)
      this.running = false
    },
    probeStatus(id) {
      if (!this.session) return "pending"
      if (!this.session.valid) return "stale"
      const evidence = this.session.evidence.filter(item => item.probe === id).at(-1)
      return evidence ? evidence.status : "pending"
    },
    statusClass(status) {
      return {pass: "text-bg-success", fail: "text-bg-danger",
        blocked: "text-bg-dark", pending: "text-bg-warning",
        stale: "text-bg-secondary"}[status] || "text-bg-warning"
    },
    statusLabel(status) {
      return {pass: "Verified", fail: "Problem found", blocked: "Could not verify",
        pending: "Not checked", stale: "Expired"}[status] || "Not checked"
    },
    probeForFailure(reason = "") {
      if (reason.startsWith("firmware_")) return "firmware"
      if (reason.startsWith("settings_")) return "persisted_readback"
      return "port_topology"
    },
    buildReport() {
      return createBiotronInspectorReport({
        session: this.session, browserEnvironment: navigator.userAgent, ports: this.ports
      })
    },
    emitReport() {
      this.$emit("inspector_report_changed", this.session ? this.buildReport() : null)
    },
    async downloadReport() {
      if (!this.session) return
      const report = this.buildReport()
      const canonical = JSON.stringify(report)
      report.report_sha256 = await sha256Hex(canonical, crypto)
      report.checksum = {algorithm: "SHA-256", scope: "canonical JSON before checksum field", value: report.report_sha256}
      const blob = new Blob([JSON.stringify(report, null, 2)], {type: "application/json"})
      const link = document.createElement("a")
      link.href = URL.createObjectURL(blob)
      link.download = `biotron-safe-inspection-${new Date().toISOString().slice(0, 10)}.json`
      link.click()
      URL.revokeObjectURL(link.href)
    }
  }
}
</script>

<style scoped>
.safe-inspector { background: #f8f9fa; font-size: 1rem; }
.safe-inspector button { min-height: 44px; }
.safe-details summary { cursor: pointer; min-height: 44px; display: flex; align-items: center; }
</style>
